export const DETAILS = {
  RISK: 'risk',
  EXPOSURE: 'exposure',
  SUMMARY: 'summary',
} as const;

export type Detail = (typeof DETAILS)[keyof typeof DETAILS];

export const supplychainKeys = {
  all: ['supplychain'] as const,

  supplier: (id: number) => [...supplychainKeys.all, 'supplier', id] as const,

  summary: () => [...supplychainKeys.all, 'suppliers', 'summary'] as const,

  logo: (id: number) => [...supplychainKeys.supplier(id), 'logo'] as const,

  detail: (id: number, detail: Detail, pageIndex?: number) =>
    [...supplychainKeys.supplier(id), { detail, ...(pageIndex ? { pageIndex } : {}) }] as const,

  usernames: () => [...supplychainKeys.all, 'usernames'] as const,

  relatedIncidents: (detail: Detail) =>
    [...supplychainKeys.all, detail, 'related_incidents'] as const,

  monitoringOverview: (clientId: number) =>
    [...supplychainKeys.all, 'monitoring-overview', clientId] as const,

  monitoringIncidentsAgg: (clientId: number, supplierIds: ReadonlyArray<number>) =>
    [
      ...supplychainKeys.monitoringOverview(clientId),
      'incidents-agg',
      [...supplierIds].sort((a, b) => a - b),
    ] as const,

  monitoringNewIncidentsAgg: (clientId: number, supplierIds: ReadonlyArray<number>) =>
    [
      ...supplychainKeys.monitoringOverview(clientId),
      'new-incidents-agg',
      [...supplierIds].sort((a, b) => a - b),
    ] as const,

  monitoringPortfolioScore: (clientId: number) =>
    [...supplychainKeys.monitoringOverview(clientId), 'portfolio-score'] as const,

  /** Prefix matching all per-supplier monitoring queries (counts, facets, …). */
  monitoringSuppliers: () => [...supplychainKeys.all, 'monitoring-supplier'] as const,

  monitoringSupplier: (supplierId: number) =>
    [...supplychainKeys.monitoringSuppliers(), supplierId] as const,

  monitoringSupplierIncidentCounts: (supplierId: number) =>
    [...supplychainKeys.monitoringSupplier(supplierId), 'incident-counts'] as const,

  monitoringSupplierIncidentFacets: (
    supplierId: number,
    clientId: number,
    incidentTypes: ReadonlyArray<string>,
    search: string,
    dateRange: string
  ) =>
    [
      ...supplychainKeys.monitoringSupplier(supplierId),
      'incident-facets',
      clientId,
      [...incidentTypes].sort(),
      search,
      dateRange,
    ] as const,

  monitoringSupplierRiskAssessments: (supplierId: number) =>
    [...supplychainKeys.monitoringSupplier(supplierId), 'risk-assessments'] as const,

  monitoringSupplierRiskAssessmentsList: (
    supplierId: number,
    params: { filter?: readonly string[]; start?: number; length?: number; order?: string }
  ) => [...supplychainKeys.monitoringSupplierRiskAssessments(supplierId), 'list', params] as const,

  monitoringSupplierRiskAssessmentCounts: (supplierId: number) =>
    [...supplychainKeys.monitoringSupplierRiskAssessments(supplierId), 'counts'] as const,

  monitoringSupplierRiskAssessmentFacets: (
    supplierId: number,
    assessmentType: string,
    search: string,
    dateRange: string
  ) =>
    [
      ...supplychainKeys.monitoringSupplierRiskAssessments(supplierId),
      'facets',
      assessmentType,
      search,
      dateRange,
    ] as const,

  monitoringSupplierRiskAssessment: (supplierId: number, assessmentId: number) =>
    [...supplychainKeys.monitoringSupplier(supplierId), 'risk-assessment', assessmentId] as const,

  monitoringSupplierRiskAssessmentAttachments: (supplierId: number, assessmentId: number) =>
    [
      ...supplychainKeys.monitoringSupplierRiskAssessment(supplierId, assessmentId),
      'attachments',
    ] as const,

  monitoringSupplierRiskScoreSeries: (supplierId: number, clientId: number) =>
    [...supplychainKeys.monitoringSupplier(supplierId), 'risk-score-series', clientId] as const,
};
